// File: src/pages/NetworkingOpportunitiesPage.jsx
// Description: Responsive UI for displaying markdown results with copy & PDF export features.

import React, { useRef, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Stack,
  Snackbar,
  useMediaQuery,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTheme } from '@mui/material/styles';

const NetworkingOpportunitiesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const contentRef = useRef();
  const [copied, setCopied] = useState(false);

  // Retrieve the result from the navigation state
  const { result } = location.state || {};

  // Determine what to display
  let displayText = "No networking opportunities found.";
  if (result) {
    if (typeof result === "object" && result.raw) {
      displayText = result.raw;
    } else if (typeof result === "object") {
      displayText = JSON.stringify(result, null, 2);
    } else {
      displayText = result;
    }
  }

  // Handle PDF Export
  const handleExportPdf = async () => {
    const canvas = await html2canvas(contentRef.current);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = (canvas.height * pageWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 10, pageWidth, pageHeight);
    pdf.save('networking-opportunities.pdf');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: isMobile ? 2 : 4 }}>
      <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
        🤝 Networking Opportunities
      </Typography>

      <Paper
        ref={contentRef}
        sx={{
          p: isMobile ? 2 : 4,
          mt: 3,
          mb: 3,
          backgroundColor: '#fefefe',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: 2,
          overflowX: 'auto'
        }}
        elevation={3}
      >
        <Box sx={{ typography: 'body1', lineHeight: 1.8 }}>
          <ReactMarkdown>{displayText}</ReactMarkdown>
        </Box>
      </Paper>

      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <CopyToClipboard text={displayText} onCopy={() => setCopied(true)}>
          <Button variant="outlined" color="secondary">
            📋 Copy to Clipboard
          </Button>
        </CopyToClipboard>

        <Button variant="outlined" color="success" onClick={handleExportPdf}>
          📄 Export as PDF
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/dashboard')}
        >
          🔙 Back to Dashboard
        </Button>
      </Stack>

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        message="Copied to clipboard!"
      />
    </Container>
  );
};

export default NetworkingOpportunitiesPage;
